import { useState } from 'react';
import { Link } from 'react-router';
import { FEATURE_SETS, formatPhoneNumber } from '@stitch/pbx-adapter/catalog';
import { api, type ImportField, type ImportPlanBody, type ImportRowView, type ImportView } from '../api/client';
import { holdChanges } from '../lib/changes';
import { plural } from '../lib/format';
import { Icon } from '../lib/icons';
import { Button, Chip } from './ui';

/** Where an import is: reviewing, adding part by part, added, undone. */
export type ImportPhase =
  | { phase: 'review' }
  | { phase: 'adding'; done: number }
  | { phase: 'added' }
  | { phase: 'failed'; message: string }
  | { phase: 'undoing' }
  | { phase: 'undone' }
  | { phase: 'discarded' }
  /** Leland planned it again: the newer card stands. */
  | { phase: 'replaced' };

export interface ImportState {
  view: ImportView;
  status: ImportPhase;
  /** Parts applied so far (change set ids), for undo. */
  applied: string[];
}

export const startImport = (view: ImportView): ImportState => ({ view, status: { phase: 'review' }, applied: [] });

const FIELD_ORDER: { field: ImportField; label: string }[] = [
  { field: 'fullName', label: 'Full name' },
  { field: 'firstName', label: 'First name' },
  { field: 'lastName', label: 'Last name' },
  { field: 'extension', label: 'Extension' },
  { field: 'email', label: 'Email' },
  { field: 'mobile', label: 'Mobile' },
  { field: 'featureSet', label: 'Kind of user' }
];
const KIND = (v: string) => FEATURE_SETS.find((f) => f.value === v)?.label ?? 'Standard';

type Filter = 'all' | 'new' | 'problem' | 'exists';
const SHOWN = 8;

/**
 * People from a spreadsheet, checked and ready to add: which columns were used (change any), each
 * row with what will happen to it, the welcome email switch, then Add (part by part) and Undo.
 */
export function ImportCard({
  domain,
  state,
  update,
  viaLeland,
  base
}: {
  domain: string;
  state: ImportState;
  update: (fn: (s: ImportState) => ImportState) => void;
  viaLeland: boolean;
  base: string;
}) {
  const { view, status } = state;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<Filter>(view.counts.problem ? 'problem' : 'all');
  const [all, setAll] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [startAt, setStartAt] = useState(view.options.firstExtension);
  /** The email switch moves at once; the plan catches up. */
  const [emailsWanted, setEmailsWanted] = useState<boolean | null>(null);
  const reviewing = status.phase === 'review';

  const replan = async (body: ImportPlanBody) => {
    setBusy(true);
    setError('');
    try {
      const r = await api.planImport(domain, view.id, body, viaLeland);
      update((s) => ({ ...s, view: r.import }));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const add = async () => {
    const parts = view.parts;
    update((s) => ({ ...s, status: { phase: 'adding', done: 0 } }));
    // Every screen loads again once, when the last part is in (not after each part).
    const release = holdChanges();
    try {
      await addParts(parts);
    } finally {
      release();
    }
  };

  const addParts = async (parts: typeof view.parts) => {
    for (let i = 0; i < parts.length; i++) {
      try {
        const r = await (viaLeland ? api.applyForLeland : api.apply)(parts[i]!.changeSetId);
        if (r.changeSet.status !== 'applied') {
          const added = parts.slice(0, i).reduce((n, p) => n + p.people, 0);
          update((s) => ({
            ...s,
            status: { phase: 'failed', message: `${added ? `${plural(added, 'person was', 'people were')} added. ` : ''}${r.changeSet.error ?? 'The next part couldn’t be added, so it was put back.'}` }
          }));
          return;
        }
        update((s) => ({ ...s, applied: [...s.applied, parts[i]!.changeSetId], status: { phase: 'adding', done: i + 1 } }));
      } catch (err) {
        update((s) => ({ ...s, status: { phase: 'failed', message: err instanceof Error ? err.message : 'That didn’t work.' } }));
        return;
      }
    }
    update((s) => ({ ...s, status: { phase: 'added' } }));
  };

  const undo = async () => {
    const release = holdChanges();
    try {
      await undoParts();
    } finally {
      release();
    }
  };

  const undoParts = async () => {
    const ids = [...state.applied].reverse();
    update((s) => ({ ...s, status: { phase: 'undoing' } }));
    for (const id of ids) {
      try {
        const r = await (viaLeland ? api.undoForLeland : api.undo)(id);
        if (r.undo.status !== 'applied') {
          update((s) => ({ ...s, status: { phase: 'failed', message: r.undo.error ?? 'Not everything could be undone. Change history has the details.' } }));
          return;
        }
        update((s) => ({ ...s, applied: s.applied.filter((x) => x !== id) }));
      } catch (err) {
        update((s) => ({ ...s, status: { phase: 'failed', message: err instanceof Error ? err.message : 'That didn’t work.' } }));
        return;
      }
    }
    update((s) => ({ ...s, status: { phase: 'undone' } }));
  };

  const notNow = async () => {
    for (const p of view.parts) await api.discard(p.changeSetId).catch(() => undefined);
    update((s) => ({ ...s, status: { phase: 'discarded' } }));
  };

  const rows = view.people.filter((r) => filter === 'all' || r.status === filter);
  const shown = all ? rows : rows.slice(0, SHOWN);
  const usedBy = (col: number) => FIELD_ORDER.find((f) => view.mapping[f.field] === col)?.field;
  const n = view.counts.new;
  const addedPeople = state.applied.reduce((sum, id) => sum + (view.parts.find((p) => p.changeSetId === id)?.people ?? 0), 0);

  return (
    <div className={`import-card ${status.phase}`} data-import={view.id}>
      <div className="import-head">
        <span className="import-file">
          <Icon name="upload" size={16} strokeWidth={2.2} />
          <strong>{view.fileName}</strong>
        </span>
        {view.sheets.length > 1 ? (
          <select className="input small" aria-label="Sheet" value={view.sheet} disabled={!reviewing || busy} onChange={(e) => void replan({ sheet: e.target.value })}>
            {view.sheets.map((s) => (
              <option key={s.name} value={s.name}>
                {s.name}
              </option>
            ))}
          </select>
        ) : view.fileName.toLowerCase().startsWith(view.sheet.toLowerCase()) ? null : (
          <span className="muted small-print">{view.sheet}</span>
        )}
      </div>

      <div className="import-counts" role="group" aria-label="Show">
        {(
          [
            ['all', `All ${view.people.length}`, 'neutral'],
            ['new', `${n} to add`, 'ok'],
            ['problem', `${view.counts.problem} to fix`, view.counts.problem ? 'warn' : 'neutral'],
            ['exists', `${view.counts.exists} already here`, 'neutral']
          ] as const
        ).map(([f, label]) => (
          <button key={f} type="button" className={`import-filter${filter === f ? ' on' : ''}`} aria-pressed={filter === f} onClick={() => setFilter(f)}>
            {label}
          </button>
        ))}
      </div>

      {reviewing && (
        <div className="import-columns">
          <button type="button" className="link-btn small-print" aria-expanded={columnsOpen} onClick={() => setColumnsOpen((o) => !o)}>
            {columnsOpen ? 'Hide columns' : 'Columns and options'}
          </button>
          {!columnsOpen && (
            <span className="muted small-print">
              {FIELD_ORDER.filter((f) => view.mapping[f.field] !== undefined)
                .map((f) => `${f.label}: ${view.columns[view.mapping[f.field]!]?.heading || `column ${view.columns[view.mapping[f.field]!]?.letter}`}`)
                .join(' · ')}
            </span>
          )}
          {columnsOpen && (
            <div className="import-mapping">
              {FIELD_ORDER.map(({ field, label }) => (
                <label key={field} className="field compact">
                  <span>{label}</span>
                  <select
                    className="input small"
                    value={view.mapping[field] ?? ''}
                    disabled={busy}
                    onChange={(e) => {
                      const next = { ...view.mapping };
                      const col = e.target.value === '' ? undefined : Number(e.target.value);
                      // One column, one detail: taking a column moves it here.
                      if (col !== undefined) {
                        const other = usedBy(col);
                        if (other) delete next[other];
                      }
                      if (col === undefined) delete next[field];
                      else next[field] = col;
                      if (field === 'fullName' && col !== undefined) {
                        delete next.firstName;
                        delete next.lastName;
                      }
                      if ((field === 'firstName' || field === 'lastName') && col !== undefined) delete next.fullName;
                      void replan({ mapping: next });
                    }}
                  >
                    <option value="">Not in the file</option>
                    {view.columns.map((c) => (
                      <option key={c.index} value={c.index}>
                        {c.letter}
                        {c.heading ? ` · ${c.heading}` : ''}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
              <label className="field compact">
                <span>Headings on row</span>
                <input
                  className="input small"
                  type="number"
                  min={1}
                  defaultValue={view.headerRow}
                  disabled={busy}
                  onBlur={(e) => {
                    const v = Number(e.target.value);
                    if (v >= 1 && v !== view.headerRow) void replan({ headerRow: v });
                  }}
                />
              </label>
              <label className="field compact">
                <span>New extensions start at</span>
                <input
                  className="input small"
                  inputMode="numeric"
                  placeholder="Next free"
                  value={startAt}
                  disabled={busy}
                  onChange={(e) => setStartAt(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  onBlur={() => startAt !== view.options.firstExtension && (startAt === '' || startAt.length >= 2) && void replan({ firstExtension: startAt })}
                />
              </label>
              <label className="field compact">
                <span>Everyone else is</span>
                <select className="input small" value={view.options.defaultFeatureSet} disabled={busy} onChange={(e) => void replan({ defaultFeatureSet: e.target.value })}>
                  {FEATURE_SETS.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </div>
      )}

      {view.truncated && (
        <p className="warn-text small" role="status">
          Part of this file was left out for being over a limit (
          {[view.truncated.rows ? 'rows after the first 2,000' : '', view.truncated.columns ? 'columns after the first 60' : '', view.truncated.cells ? 'everything past what one file can hold' : '']
            .filter(Boolean)
            .join(', ')}
          ). Split it into smaller files.
        </p>
      )}
      {rows.length === 0 ? (
        <p className="muted small-print import-none">{filter === 'problem' ? 'Nothing to fix.' : filter === 'exists' ? 'Nobody in the file is here already.' : 'No one to add.'}</p>
      ) : (
        <div className="table-wrap import-table">
          <table className="table compact">
            <thead>
              <tr>
                <th scope="col">Row</th>
                <th scope="col">Name</th>
                <th scope="col">Ext.</th>
                <th scope="col">Email</th>
                <th scope="col">Mobile</th>
                <th scope="col">Kind</th>
                <th scope="col">What happens</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <Row key={r.row} r={r} added={status.phase === 'added'} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rows.length > SHOWN && (
        <button type="button" className="link-btn small-print" onClick={() => setAll((a) => !a)}>
          {all ? 'Show fewer' : `Show all ${rows.length}`}
        </button>
      )}
      {view.counts.problem > 0 && reviewing && (
        <p className="muted small-print">Rows to fix are left out. Fix them in your file and upload it again, or add those people by hand afterwards.</p>
      )}

      {reviewing && n > 0 && (
        <label className="check-row import-email">
          <input
            type="checkbox"
            checked={emailsWanted ?? view.options.sendEmails}
            disabled={view.emailable === 0 || (busy && emailsWanted === null)}
            onChange={(e) => {
              const want = e.target.checked;
              setEmailsWanted(want);
              void replan({ sendEmails: want }).finally(() => setEmailsWanted(null));
            }}
          />
          <span className="cell-stack">
            <span>
              Email {view.emailable === n ? (n === 1 ? 'them' : n === 2 ? 'both of them' : `all ${n} of them`) : `the ${view.emailable} with an email address`} their login details for the apps
            </span>
            <small className="muted">{view.emailable === 0 ? 'No one in the file has an email address.' : 'Or send them later, one by one, from People. Emails can’t be taken back.'}</small>
          </span>
        </label>
      )}

      {error && (
        <p className="crit-text small" role="alert">
          {error}
        </p>
      )}

      {reviewing && (
        <div className="plan-actions">
          {n > 0 ? (
            <Button variant="primary" icon="plus" disabled={busy} onClick={() => void add()}>
              {busy ? 'Checking…' : `Add ${plural(n, 'person', 'people')}`}
            </Button>
          ) : (
            <span className="muted small-print">There’s no one new to add.</span>
          )}
          <Button variant="ghost" disabled={busy} onClick={() => void notNow()}>
            Not now
          </Button>
        </div>
      )}
      {status.phase === 'adding' && (
        <div className="import-progress" role="status">
          <progress max={view.parts.length} value={status.done} aria-label="Parts added" />
          <span className="muted small-print">
            Adding {plural(n, 'person', 'people')}
            {view.parts.length > 1 ? `, part ${Math.min(status.done + 1, view.parts.length)} of ${view.parts.length}` : ''}, and checking each one on the phone system…
          </span>
        </div>
      )}
      {status.phase === 'added' && (
        <>
          <p className="ok-text small" role="status">
            <Icon name="check" size={15} strokeWidth={2.4} /> {plural(addedPeople, 'person', 'people')} added
            {view.options.sendEmails && view.emailable ? `, and ${plural(view.emailable, 'login email', 'login emails')} sent` : ''}.
          </p>
          <div className="plan-actions">
            <Button variant="secondary" icon="undo" onClick={() => void undo()}>
              Undo all
            </Button>
            <Link className="link-btn" to={`${base}/people`}>
              People
            </Link>
            <Link className="link-btn" to={`${base}/history`}>
              Change history
            </Link>
          </div>
        </>
      )}
      {status.phase === 'undoing' && <p className="muted small-print">Undoing…</p>}
      {status.phase === 'undone' && <p className="muted small-print">Everyone from the file was taken off again. (Emails already sent can’t be taken back.)</p>}
      {status.phase === 'discarded' && <p className="muted small-print">Not added. Upload the file again any time.</p>}
      {status.phase === 'replaced' && <p className="muted small-print">Replaced by the newer plan below.</p>}
      {status.phase === 'failed' && (
        <>
          <p className="crit-text small" role="alert">
            {status.message}
          </p>
          {state.applied.length > 0 && (
            <div className="plan-actions">
              <Button variant="secondary" icon="undo" onClick={() => void undo()}>
                Undo what was added
              </Button>
              <Button variant="ghost" icon="refresh" onClick={() => void replan({}).then(() => update((s) => ({ ...s, status: { phase: 'review' }, applied: [] })))}>
                Check the rest again
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Row({ r, added }: { r: ImportRowView; added: boolean }) {
  const name = [r.firstName, r.lastName].filter(Boolean).join(' ') || '—';
  return (
    <tr className={`import-row ${r.status}`}>
      <td className="muted">{r.row}</td>
      <td>{name}</td>
      <td className="mono">
        {r.extension || '—'}
        {r.assigned && r.status === 'new' && <span className="muted small-print"> new</span>}
      </td>
      <td className="import-email-cell">{r.email || <span className="muted">—</span>}</td>
      <td className="mono">{r.mobile ? formatPhoneNumber(r.mobile) : <span className="muted">—</span>}</td>
      <td>{KIND(r.featureSet)}</td>
      <td>
        <span className="cell-stack">
          <Chip small tone={r.status === 'new' ? 'ok' : r.status === 'problem' ? 'warn' : 'neutral'}>
            {r.status === 'new' ? (added ? 'Added' : 'Add') : r.status === 'problem' ? 'Fix' : 'Already here'}
          </Chip>
          {r.note && r.status !== 'exists' && <small>{r.note}</small>}
          {r.status === 'exists' && r.note && r.note !== 'Already here' && <small className="muted">{r.note.replace('Already here as', 'Is')}</small>}
          {r.warnings.map((w) => (
            <small key={w} className="muted">
              {w}
            </small>
          ))}
        </span>
      </td>
    </tr>
  );
}
